import {useRef, DependencyList, RefObject} from "react";
import {useLayoutEffect, useEffect} from "react";

// This hook was found here
// https://dev.to/n8tb1t/tracking-scroll-position-with-react-hooks-3bbj
// All the documentation around its usage is on the website. Only formatting and minor changes was made to adapt for Ryman

export const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

interface IPosition {
  x: number;
  y: number;
}

interface IScrollProps {
  prevPos: IPosition;
  currPos: IPosition;
}

type ElementRef = RefObject<HTMLElement | null>;

const isBrowser = typeof window !== `undefined`;
const zeroPosition = {x: 0, y: 0};

const getClientRect = (element?: HTMLElement) =>
  element?.getBoundingClientRect();

const getScrollPosition = ({
  element,
  boundingElement
}: {
  element?: ElementRef;
  boundingElement?: ElementRef;
}) => {
  if (!isBrowser) {
    return zeroPosition;
  }

  const targetPosition = getClientRect(element?.current || document.body);
  const containerPosition = getClientRect(boundingElement?.current!);

  if (!targetPosition) {
    return zeroPosition;
  }

  return containerPosition
    ? {
        x: (containerPosition.x || 0) - (targetPosition.x || 0),
        y: (containerPosition.y || 0) - (targetPosition.y || 0)
      }
    : {x: targetPosition.left, y: targetPosition.top};
};

export const useScrollPosition = (
  effect: (props: IScrollProps) => void,
  boundingElement: ElementRef,
  element: ElementRef,
  deps?: DependencyList,
  wait?: number
): void => {
  const position = useRef(getScrollPosition({boundingElement}));

  let throttleTimeout: number | null = null;

  const callBack = () => {
    const currPos = getScrollPosition({element, boundingElement});
    effect({prevPos: position.current, currPos});
    position.current = currPos;
    throttleTimeout = null;
  };

  useIsomorphicLayoutEffect(() => {
    if (!isBrowser) {
      return undefined;
    }

    const handleScroll = () => {
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = window.setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    boundingElement.current?.addEventListener("scroll", handleScroll, {
      passive: true
    });

    return () => {
      boundingElement.current?.removeEventListener("scroll", handleScroll);

      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, deps);
};

useScrollPosition.defaultProps = {
  deps: [],
  boundingElement: false,
  element: false,
  wait: null
};
